import { storeToRefs } from 'pinia'
import type { AuthData } from '@/types/Auth'

export const useAuth = () => {
  const authStore = useAuthStore()
  const { encryptLoginPayload } = useClientCrypto()

  // État réactif
  const {
    authenticated,
    loading,
    user,
    token
  } = storeToRefs(authStore)

  // Getters réactifs
  const userId = computed(() => authStore.userId)
  const userEmail = computed(() => authStore.userEmail)
  const userLogin = computed(() => authStore.userLogin)
  const userSlug = computed(() => authStore.userSlug)
  const userType = computed(() => authStore.userType)
  const isActive = computed(() => authStore.isActive)
  const isArchived = computed(() => authStore.isArchived)
  const isSuspended = computed(() => authStore.isSuspended)
  const isDeleted = computed(() => authStore.isDeleted)
  const userGroups = computed(() => authStore.userGroups)
  const userPermissions = computed(() => authStore.userPermissions)
  const authToken = computed(() => authStore.authToken)

  // Actions
  const login = async (credentials: AuthData) => {
    return await authStore.authenticateUser(credentials)
  }
  const loginBis = async (credentials: AuthData) => {
    return await authStore.authenticateUserBis(credentials)
  }

  const loginEncrypted = async (login: string, password: string) => {
    const cipher = await encryptLoginPayload(login, password)
    return await $fetch('/auth/login-encrypted', {
      method: 'POST',
      credentials: 'include',   // indispensable pour cookie de session Django
      body: { cipher }
    })
  }

  const logout = () => {
    authStore.logUserOut()
    // Nettoyer aussi le profil
    const profileStore = useProfileStore()
    profileStore.clearProfile()
  }

  const updateUserData = (userData: any) => {
    authStore.updateUser(userData)
  }

  // Fonctions utilitaires
  const hasPermission = (codename: string): boolean => {
    return authStore.hasPermission(codename)
  }

  const hasGroup = (groupName: string): boolean => {
    return authStore.hasGroup(groupName)
  }

  const canView = (resource: string): boolean => {
    return hasPermission(`view_${resource}`)
  }

  const canAdd = (resource: string): boolean => {
    return hasPermission(`add_${resource}`)
  }

  const canChange = (resource: string): boolean => {
    return hasPermission(`change_${resource}`)
  }

  const canDelete = (resource: string): boolean => {
    return hasPermission(`delete_${resource}`)
  }

  const isStudent = (): boolean => {
    return hasGroup('Etudiant')
  }

  const isStaff = (): boolean => {
    // Exclure le superadmin de la logique restrictive du staff
    if (userType.value === 'superadmin') return false
    return hasGroup('Personnel') || hasGroup('Enseignant') || hasGroup('Secrétaire') ||
        hasGroup('Comptable') || hasGroup('RH') || hasGroup('Responsable pedagogique')
  }

  const isAdmin = (): boolean => {
    return hasGroup('Admin') || hasGroup('Administrateur') || hasGroup('Superviseur')
  }

  const isEnseignant = (): boolean => {
    return hasGroup('Enseignant')
  }

  const isComptable = (): boolean => {
    return hasGroup('Comptable')
  }

  const isRH = (): boolean => {
    return hasGroup('RH')
  }

  const isResponsablePedagogique = (): boolean => {
    return hasGroup('Responsable pedagogique')
  }

  const isSuperAdmin = (): boolean => {
    return userType.value === 'superadmin'
  }

  return {
    // État
    authenticated,
    loading,
    user,
    token,

    // Données utilisateur
    userId,
    userEmail,
    userLogin,
    userSlug,
    userType,
    isActive,
    isArchived,
    isSuspended,
    isDeleted,
    userGroups,
    userPermissions,
    authToken,

    // Actions
    login,
    loginEncrypted,
    loginBis,
    logout,
    updateUserData,

    // Vérifications
    hasPermission,
    hasGroup,
    canView,
    canAdd,
    canChange,
    canDelete,
    isStudent,
    isStaff,
    isAdmin,
    isEnseignant,
    isComptable,
    isRH,
    isResponsablePedagogique,
    isSuperAdmin,
  }
}
